import Image from 'next/image';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useSettings } from '@/contexts/settings';

import styles from './HelpReservationModal.module.css';

const RESERVATION_WHATSAPP_URL =
  'https://api.whatsapp.com/send/?phone=558188241621&text=Ol%C3%A1%21+Gostaria+de+alugar+um+carro+pela+Foco.&type=phone_number&app_absent=0';

type ModalView = 'reservation' | 'qrCode';

type HelpAction = {
  label: ReactNode;
  onClick: () => void;
};

const pageMatchers: Record<string, (pathname: string) => boolean> = {
  todas: () => true,
  all: () => true,
  inicio: pathname => pathname === '/',
  home: pathname => pathname === '/',
  veiculos: pathname => pathname.startsWith('/veiculos'),
  vehicles: pathname => pathname.startsWith('/veiculos'),
  extras: pathname => pathname.startsWith('/extras'),
  condutor: pathname => pathname.startsWith('/condutor'),
  driver: pathname => pathname.startsWith('/condutor'),
  pagamento: pathname => pathname.startsWith('/pagamento'),
  payment: pathname => pathname.startsWith('/pagamento'),
  reserva: pathname =>
    pathname.startsWith('/reserva') || pathname.startsWith('/criar-reserva'),
  reservation: pathname =>
    pathname.startsWith('/reserva') || pathname.startsWith('/criar-reserva'),
  desativado: () => false,
  disabled: () => false,
};

function WhatsAppIcon() {
  return (
    <svg
      aria-hidden="true"
      className={styles.whatsIcon}
      viewBox="0 0 32 32"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M16 3.2A12.6 12.6 0 0 0 5.2 22.3L3.6 28.8l6.6-1.7A12.5 12.5 0 0 0 16 28.6 12.7 12.7 0 0 0 16 3.2Zm0 22.9c-1.8 0-3.5-.5-5-1.4l-.4-.2-3.9 1 1-3.8-.3-.4A10.2 10.2 0 1 1 16 26.1Zm5.6-7.7c-.3-.2-1.8-.9-2.1-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6 0-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9s1.2 3.3 1.4 3.5c.2.2 2.4 3.7 5.8 5.2.8.3 1.4.5 1.9.7.8.3 1.5.2 2.1.1.6-.1 1.8-.7 2.1-1.5.3-.7.3-1.4.2-1.5-.1-.1-.3-.2-.6-.4Z"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      className={styles.closeIcon}
      viewBox="0 0 24 24"
      focusable="false"
    >
      <path
        d="m5 5 14 14M19 5 5 19"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      aria-hidden="true"
      className={styles.arrowIcon}
      viewBox="0 0 24 24"
      focusable="false"
    >
      <path
        d="m9 6 6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

export default function HelpReservationModal() {
  const router = useRouter();
  const { helpModalPage, helpModalIdleTimeSeconds } = useSettings();
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [view, setView] = useState<ModalView>('reservation');
  const [qrCodeUrl, setQrCodeUrl] = useState(RESERVATION_WHATSAPP_URL);

  const shouldRenderOnPage = useMemo(() => {
    const configuredPage = helpModalPage || 'todas';
    const matcher = pageMatchers[configuredPage];

    if (matcher) {
      return matcher(router.pathname);
    }

    if (configuredPage.startsWith('/')) {
      return router.pathname === configuredPage;
    }

    return false;
  }, [helpModalPage, router.pathname]);

  const idleTimeMs = useMemo(() => {
    const seconds = Number(helpModalIdleTimeSeconds) || 15;
    return Math.max(seconds, 1) * 1000;
  }, [helpModalIdleTimeSeconds]);

  useEffect(() => {
    setIsVisible(false);
    setIsExpanded(false);
    setView('reservation');
  }, [router.pathname]);

  useEffect(() => {
    if (!shouldRenderOnPage) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const startIdleTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsVisible(true);
      }, idleTimeMs);
    };

    const activityEvents = [
      'click',
      'keydown',
      'mousemove',
      'scroll',
      'touchstart',
    ];

    activityEvents.forEach(eventName => {
      window.addEventListener(eventName, startIdleTimer, { passive: true });
    });

    startIdleTimer();

    return () => {
      clearTimeout(timeoutId);
      activityEvents.forEach(eventName => {
        window.removeEventListener(eventName, startIdleTimer);
      });
    };
  }, [idleTimeMs, shouldRenderOnPage]);

  if (!shouldRenderOnPage || !isVisible) {
    return null;
  }

  const openQrCode = (url: string) => {
    setQrCodeUrl(url);
    setView('qrCode');
  };

  const closeModal = () => {
    setIsVisible(false);
    setIsExpanded(false);
    setView('reservation');
  };

  const backToReservationOptions = () => {
    setView('reservation');
  };

  const collapseModal = () => {
    setIsExpanded(false);
    setView('reservation');
  };

  const reservationActions: HelpAction[] = [
    {
      label: <>Reserva at&eacute; 27 dias</>,
      onClick: () => openQrCode(RESERVATION_WHATSAPP_URL),
    },
    {
      label: 'Mensal Flex (a partir de 28 dias)',
      onClick: () => openQrCode(RESERVATION_WHATSAPP_URL),
    },
    {
      label: 'Motorista de Aplicativo',
      onClick: () => openQrCode(RESERVATION_WHATSAPP_URL),
    },
  ];

  return (
    <div
      className={`${styles.container} ${isExpanded ? styles.expanded : ''}`}
      aria-live="polite"
    >
      {!isExpanded ? (
        <button
          className={styles.launcher}
          type="button"
          onClick={() => {
            setView('reservation');
            setIsExpanded(true);
          }}
        >
          <WhatsAppIcon />
          <span>PRECISA DE AJUDA?</span>
        </button>
      ) : (
        <section className={styles.panel} aria-label="Atendimento WhatsApp">
          <header className={styles.header}>
            <div className={styles.title}>
              <WhatsAppIcon />
              <span className={styles.desktopTitle}>FA&Ccedil;A SUA RESERVA</span>
              <span className={styles.mobileTitle}>FA&Ccedil;A SUA RESERVA</span>
            </div>
            <button
              className={styles.closeButton}
              type="button"
              aria-label="Fechar atendimento"
              onClick={closeModal}
            >
              <CloseIcon />
            </button>
          </header>

          {view === 'qrCode' ? (
            <div className={`${styles.content} ${styles.qrContent}`}>
              <p className={styles.qrText}>
                Escaneie o QRCode para entrar em contato com nosso suporte pelo
                WhatsApp no seu celular
              </p>
              <Image
                src="/qr-rac.png"
                width={178}
                height={178}
                alt="QRCode para atendimento pelo WhatsApp"
                className={styles.qrImage}
              />
              <p className={styles.orText}>Ou</p>
              <a
                className={`${styles.webButton} ${styles.successButton}`}
                href={qrCodeUrl}
                target="_blank"
                rel="noreferrer"
              >
                Abrir no WhatsApp Web
              </a>
              <button
                className={styles.backButton}
                type="button"
                onClick={backToReservationOptions}
              >
                Voltar
              </button>
            </div>
          ) : (
            <div className={styles.content}>
              {reservationActions.map((action, index) => (
                <button
                  key={`reservation-${index}`}
                  className={styles.actionItem}
                  type="button"
                  onClick={action.onClick}
                >
                  <span>{action.label}</span>
                  <ArrowIcon />
                </button>
              ))}

              <button
                className={styles.backButton}
                type="button"
                onClick={collapseModal}
              >
                Voltar
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
